import { espOperationValid } from './review-esp.js';

export const ESP_SOURCE_DIFF_LIMITS = Object.freeze({ sourceBytes: 900000, lines: 12000, cells: 1000000, displayedRows: 500 });
const SHA = /^[a-f0-9]{64}$/;
const verified = new WeakSet();
const fail = code => { throw Object.assign(Error(code), { code }); };
const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
function sourceBytes(text) {
  if (typeof text !== 'string' || !text.length || text.includes('\0')) fail('esp_diff_source_invalid');
  if (text.length > ESP_SOURCE_DIFF_LIMITS.sourceBytes) fail('esp_diff_source_too_large');
  const bytes = encoder.encode(text);
  if (bytes.length > ESP_SOURCE_DIFF_LIMITS.sourceBytes) fail('esp_diff_source_too_large');
  if (decoder.decode(bytes) !== text) fail('esp_diff_source_invalid');
  return bytes;
}
async function digest(bytes, cryptoImpl) {
  if (typeof cryptoImpl?.subtle?.digest !== 'function') fail('esp_diff_verification_unavailable');
  try {
    const raw = new Uint8Array(await cryptoImpl.subtle.digest('SHA-256', bytes));
    if (raw.length !== 32) fail('esp_diff_verification_unavailable');
    return Array.from(raw, v => v.toString(16).padStart(2, '0')).join('');
  } catch { fail('esp_diff_verification_unavailable'); }
}

// Caller supplies the previously selected operation pin. Capture primitives
// before awaiting; neither a later object mutation nor a current editor draft
// can replace one half of the verified pair.
export async function verifyEspSourcePair(pair, { jobId, expected, cryptoImpl = globalThis.crypto } = {}) {
  if (!expected || !['operation_id', 'operation_digest', 'output_digest'].every(k => SHA.test(expected[k] || '')))
    fail('esp_diff_identity_required');
  if (!espOperationValid(pair, { jobId, expected, pair: true })) fail('esp_diff_pair_unbound');
  const value = { schema: 'EspVerifiedSourcePair.v1', job_id: pair.job_id, workspace_id: pair.workspace_id,
    client_slug: pair.client_slug, operation_id: pair.operation_id, operation_digest: pair.operation_digest,
    input_digest: pair.input_digest, executor_digest: pair.executor_digest, output_digest: pair.output_digest,
    revision: pair.revision, revision_digest: pair.revision_digest, esp: pair.esp, esp_tier: pair.esp_tier,
    neutral_html_digest: pair.output.neutral_html_digest, integrated_html_digest: pair.output.integrated_html_digest,
    neutral_html: pair.neutral_html, integrated_html: pair.integrated_html };
  if (![value.neutral_html_digest, value.integrated_html_digest].every(v => typeof v === 'string' && SHA.test(v))) fail('esp_diff_pair_unbound');
  // Production callers retain the selected status or board's source pins.
  // Identity-only callers remain supported, but cannot supply independent HTML
  // pins; hashing alone only verifies each source against its returned receipt.
  for (const [pin, actual] of [
    [expected.output?.neutral_html_digest, value.neutral_html_digest],
    [expected.output?.integrated_html_digest, value.integrated_html_digest],
    [expected.fingerprints?.neutral_html_digest, value.neutral_html_digest],
    [expected.fingerprints?.template_html_digest, value.integrated_html_digest],
  ]) if (pin !== undefined && (typeof pin !== 'string' || !SHA.test(pin) || pin !== actual)) fail('esp_diff_source_pin_changed');
  const before = sourceBytes(value.neutral_html), after = sourceBytes(value.integrated_html);
  const [neutralHash, integratedHash] = await Promise.all([digest(before, cryptoImpl), digest(after, cryptoImpl)]);
  if (neutralHash !== value.neutral_html_digest) fail('esp_diff_neutral_digest_mismatch');
  if (integratedHash !== value.integrated_html_digest) fail('esp_diff_integrated_digest_mismatch');
  value.neutral_bytes = before.length; value.integrated_bytes = after.length;
  Object.freeze(value); verified.add(value); return value;
}
export const isVerifiedEspSourcePair = value => !!value && verified.has(value);

function lineCount(text) {
  let n = 1;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) n++;
  return n;
}
// Deterministic LCS alignment within hard byte/line/cell budgets. No regex
// execution or HTML parsing. A budget refusal returns source_only, not an
// approximation disguised as a complete diff. Line endings are never rewritten.
export function buildEspLineDiff(before, after, { maxRows = ESP_SOURCE_DIFF_LIMITS.displayedRows } = {}) {
  sourceBytes(before); sourceBytes(after);
  if (!Number.isInteger(maxRows) || maxRows < 1 || maxRows > ESP_SOURCE_DIFF_LIMITS.displayedRows) fail('esp_diff_options_invalid');
  const beforeLines = lineCount(before), afterLines = lineCount(after), identical = before === after;
  const fallback = reason => ({ mode: 'source_only', reason, identical, rows: [], totalRows: null, omittedRows: null,
    additions: null, removals: null, beforeLines, afterLines });
  if (Math.max(beforeLines, afterLines) > ESP_SOURCE_DIFF_LIMITS.lines) return fallback('line_budget_exceeded');
  const a = before.split('\n'), b = after.split('\n');
  let prefix = 0, suffix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix++;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) suffix++;
  const n = a.length - prefix - suffix, m = b.length - prefix - suffix;
  if (n && m && (n + 1) * (m + 1) > ESP_SOURCE_DIFF_LIMITS.cells) return fallback('alignment_budget_exceeded');
  // Intern lines so the dynamic-programming bound counts constant-time integer
  // comparisons, not repeated comparisons of potentially long source strings.
  const ids = new Map();
  const id = text => { if (!ids.has(text)) ids.set(text, ids.size + 1); return ids.get(text); };
  const ai = a.slice(prefix, a.length - suffix).map(id), bi = b.slice(prefix, b.length - suffix).map(id);
  const width = m + 1, grid = n && m ? new Uint32Array((n + 1) * width) : null;
  if (grid) for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
    grid[i * width + j] = ai[i] === bi[j] ? 1 + grid[(i + 1) * width + j + 1] : Math.max(grid[(i + 1) * width + j], grid[i * width + j + 1]);
  const rows = []; let totalRows = 0, additions = 0, removals = 0;
  const append = (kind, text, beforeLine, afterLine) => {
    totalRows++; if (kind === 'add') additions++; if (kind === 'del') removals++;
    if (rows.length < maxRows) rows.push({ kind, text, beforeLine, afterLine });
  };
  for (let i = 0; i < prefix; i++) append('ctx', a[i], i + 1, i + 1);
  let i = 0, j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && ai[i] === bi[j]) { append('ctx', a[prefix + i], prefix + i + 1, prefix + j + 1); i++; j++; }
    else if (i < n && (j === m || grid && grid[(i + 1) * width + j] >= grid[i * width + j + 1])) {
      append('del', a[prefix + i], prefix + i + 1, null); i++;
    } else { append('add', b[prefix + j], null, prefix + j + 1); j++; }
  }
  for (let k = suffix; k > 0; k--) append('ctx', a[a.length - k], a.length - k + 1, b.length - k + 1);
  return { mode: 'aligned', reason: null, identical, rows, totalRows, omittedRows: totalRows - rows.length, additions, removals, beforeLines, afterLines };
}

export function espSourceDiffMessage(code) {
  return ({ esp_diff_identity_required: 'Open an exact retained operation before comparing its sources.',
    esp_diff_pair_unbound: 'The returned files do not match the selected operation. Reload the candidate.',
    esp_diff_source_pin_changed: 'The source fingerprints changed from the selected operation. No comparison is shown. Reload the candidate.',
    esp_diff_source_invalid: 'A retained source is not valid UTF-8 text. No comparison is shown.',
    esp_diff_source_too_large: 'A retained source exceeds the 900,000-byte review limit. No comparison is shown.',
    esp_diff_verification_unavailable: 'Source fingerprint verification is unavailable. Use a secure browser context and retry.',
    esp_diff_neutral_digest_mismatch: 'The neutral source fingerprint does not match its retained receipt. No comparison is shown.',
    esp_diff_integrated_digest_mismatch: 'The converted source fingerprint does not match its retained receipt. No comparison is shown.',
  })[code] || 'The retained source comparison could not be verified. Reload the candidate.';
}
